import type {
  CapabilityMap,
  OptimizationInitProperties,
  WebShellClient,
  WebShellClientOptions,
  WebShellOptimization,
  WebShellUnstable
} from "@nike/web-shell-types";
import { DotcomWebShellLocale } from "./locale";
import { DotcomWebShellTranslations } from "./translations";
import { ModalManager, defaultModalRoot } from "./modals";
import { DotcomWebShellUserLocation } from "./userLocation";
import { defaultAnalyticsOptions, dotcomWebShellAnalyticsFactory } from "./analytics";
import { makeIdentity } from "./identity/identity";
import { getWebShellClientInfo } from "./internal/webShellClientInfo";
import { DotcomWebShellQueueOptimization } from "./optimization/queue/queueOptimization";
import { PageContextStore } from "./pageContext/pageContextStore";

export const defaultWebShellClientOptions: WebShellClientOptions = {
  appName: `Web Shell Application`,
  initialLocale: DotcomWebShellLocale.defaultLocale,
  modal: { rootSelector: defaultModalRoot },
  isProduction: false,
  analytics: defaultAnalyticsOptions,
  optimization: {
    locations: []
  }
};

export class WebShellClientFactory {
  static getInstance = (options: WebShellClientOptions): WebShellClient => {
    const webShellClientVersion = process.env.WEB_SHELL_CLIENT_VERSION;
    const locale = new DotcomWebShellLocale(options.initialLocale);
    const userLocation = new DotcomWebShellUserLocation();
    const translations = new DotcomWebShellTranslations(locale);
    const modalManager = new ModalManager(options.modal?.rootSelector);
    const identity = makeIdentity();

    const optimizationInstance = WebShellClientFactory.getDotcomWebShellOptimization(
      options.optimization
    );

    const analytics = dotcomWebShellAnalyticsFactory(
      options.analytics ?? defaultWebShellClientOptions.analytics
    );

    if (typeof window !== `undefined`) {
      window.newrelic?.addPageAction(`WEB_SHELL_CLIENT_INITIALIZED`, {
        webShellClientVersion: webShellClientVersion ?? ``,
        webShellNextVersion: window.WSN_VERSION ?? ``
      });
    }

    const pageContext = new PageContextStore();

    return {
      __VERSION__: webShellClientVersion,
      appName: options.appName ?? ``,
      analytics,
      identity,
      locale,
      modalManager,
      translations,
      optimization: {
        getLocations: optimizationInstance.getLocations,
        setHandler: optimizationInstance.setHandler
      },
      pageContext,
      userLocation,
      isProduction: options.isProduction ?? defaultWebShellClientOptions.isProduction,
      __WS_INTERNAL__: { getInfo: getWebShellClientInfo },
      __WS_UNSTABLE__: WebShellClientFactory.makeUnstable()
    };
  };

  private static makeUnstable = (): WebShellUnstable => {
    const cache = new Map<string, Promise<unknown>>();

    const loaders: Record<keyof CapabilityMap, () => Promise<unknown>> = {
      product: async () => {
        const { DotcomProductCapability } = await import(
          /* webpackChunkName: "product-capability" */ `./product/capability`
        );
        return new DotcomProductCapability();
      }
    };

    return {
      getCapability<K extends keyof CapabilityMap>(name: K): Promise<CapabilityMap[K]> {
        const cached = cache.get(name);
        if (cached) return cached as Promise<CapabilityMap[K]>;

        const loader = loaders[name];
        if (!loader) {
          return Promise.reject(new Error(`Unknown capability: ${String(name)}`));
        }

        const promise = loader().catch((error: unknown) => {
          cache.delete(name);
          throw error;
        });
        cache.set(name, promise);
        return promise as Promise<CapabilityMap[K]>;
      }
    };
  };

  private static getDotcomWebShellOptimization = (
    options?: OptimizationInitProperties
  ): WebShellOptimization => {
    return new DotcomWebShellQueueOptimization(options?.locations);
  };
}
